import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import './Home.css';

const categories = [
  {
    title: "Technology",
    description: "Software, IT & Development",
    count: "500+ jobs",
    icon: "computer",
    iconBgColor: "#DBEAFE",
  },
  {
    title: "Marketing",
    description: "Digital & Content Marketing",
    count: "200+ jobs",
    icon: "bar_chart",
    iconBgColor: "#FCE7F3",
  },
  {
    title: "Design",
    description: "UI/UX & Graphic Design",
    count: "150+ jobs",
    icon: "palette",
    iconBgColor: "#e9f4fd",
  },
  {
    title: "Business",
    description: "Management & Operations",
    count: "300+ jobs",
    icon: "business_center",
    iconBgColor: "#FEF3C7",
  },
  {
    title: "Finance",
    description: "Accounting & Banking",
    count: "180+ jobs",
    icon: "currency_rupee",
    iconBgColor: "#D1FAE5",
  },
  {
    title: "Education",
    description: "Teaching & Training",
    count: "120+ jobs",
    icon: "school",
    iconBgColor: "#CFFAFE",
  },
  {
    title: "Healthcare",
    description: "Medical & Nursing",
    count: "250+ jobs",
    icon: "medical_services",
    iconBgColor: "#FEE2E2",
  },
  {
    title: "Engineering",
    description: "Mechanical & Civil",
    count: "220+ jobs",
    icon: "engineering",
    iconBgColor: "#E2E8F0",
  },
];

const Home = () => {
  const navigate = useNavigate();
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [stats, setStats] = useState({
    totalJobs: 0,
    totalCompanies: 0,
    totalCandidates: 0,
    successfulPlacements: 0
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const searchRef = useRef(null);
  const suggestDebounce = useRef(null);

  useEffect(() => {
    fetchFeaturedJobs();
  }, []);

  const fetchFeaturedJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/jobs`, {
        params: { limit: 6 }
      });
      setFeaturedJobs(response.data.data || []);

      // Mock stats - you can replace with actual API calls
      setStats({
        totalJobs: response.data.pagination?.total || 150,
        totalCompanies: 50,
        totalCandidates: 1200,
        successfulPlacements: 350
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching featured jobs:', error);
      setLoading(false);
    }
  };

  const formatSalary = (job) => {
    if (!job.salary || !job.salary.min) return 'Not disclosed';
    const { min, max, currency, period } = job.salary;
    if (max) {
      return `${currency} ${min.toLocaleString()} - ${max.toLocaleString()} / ${period}`;
    }
    return `${currency} ${min.toLocaleString()} / ${period}`;
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    const intervals = {
      day: 86400,
      hour: 3600,
      minute: 60
    };

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);
      if (interval >= 1) {
        return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
      }
    }
    return 'Just now';
  };

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/jobs`, {
        params: { search: query.trim(), limit: 8 }
      });
      const jobs = response.data.data || [];
      // Build unique title + skill suggestions
      const titleSugs = jobs.map(j => ({ type: 'title', value: j.title, icon: 'work' }));
      const skillSugs = [];
      jobs.forEach(j => {
        (j.skills || []).slice(0, 3).forEach(s => {
          if (!skillSugs.find(x => x.value === s.name)) {
            skillSugs.push({ type: 'skill', value: s.name, icon: 'psychology' });
          }
        });
      });
      const combined = [...titleSugs, ...skillSugs]
        .filter((s, i, arr) => arr.findIndex(x => x.value === s.value) === i)
        .slice(0, 8);
      setSuggestions(combined);
      setShowSuggestions(combined.length > 0);
    } catch {
      setSuggestions([]);
    }
  }, []);

  const handleSearchQueryChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    setActiveSuggestion(-1);
    clearTimeout(suggestDebounce.current);
    suggestDebounce.current = setTimeout(() => fetchSuggestions(val), 250);
  };

  const handleSuggestionClick = (sug) => {
    setSearchQuery(sug.value);
    setSuggestions([]);
    setShowSuggestions(false);
    // navigate(`/jobs?search=${encodeURIComponent(sug.value)}`);
  };

  const handleSearchKeyDown = (e) => {
    if (!showSuggestions) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestion(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestion(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && activeSuggestion >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[activeSuggestion]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleHeroSearch = (e) => {
    e.preventDefault();

    // Build query parameters
    const params = new URLSearchParams();
    if (searchQuery.trim()) {
      params.append('search', searchQuery.trim());
    }
    if (locationQuery.trim()) {
      params.append('location', locationQuery.trim());
    }

    // Navigate to jobs page with or without query parameters
    const queryString = params.toString();
    navigate(queryString ? `/jobs?${queryString}` : '/jobs');
  };

  const handleNewsletterSubscribe = async (e) => {
    e.preventDefault();

    if (!newsletterEmail) {
      toast.error('Please enter your email address');
      return;
    }

    // Email validation
    const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    if (!emailRegex.test(newsletterEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSubscribing(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/newsletter/subscribe`, {
        email: newsletterEmail
      });

      toast.success(response.data.message || 'Successfully subscribed to newsletter!');
      setNewsletterEmail('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to subscribe. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="home-page">
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="material-symbols-outlined" style={{ fontSize: 13 }}>verified</span>
              Leading Job Platform — Trusted by People
            </div>
            <h1 className="hero-title">Find Your <span>Dream Job</span> Today</h1>
            <p className="hero-subtitle">
              Connect with top employers and discover opportunities that match your skills and aspirations
            </p>

            {/* Hero Search Bar */}
            <div className="hero-search">
              <form onSubmit={handleHeroSearch} className="search-box">
                <div className="search-input-wrapper" style={{ borderRight: '1px solid #b8b8b8' }} ref={searchRef}>
                  <span className="search-input-icon material-symbols-outlined">search</span>
                  <input
                    type="text"
                    placeholder="Job title, skill, or keyword…"
                    className="search-input"
                    value={searchQuery}
                    onChange={handleSearchQueryChange}
                    onKeyDown={handleSearchKeyDown}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    autoComplete="off"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <ul className="search-suggestions">
                      {suggestions.map((sug, i) => (
                        <li
                          key={i}
                          className={`suggestion-item${i === activeSuggestion ? ' active' : ''}`}
                          onMouseDown={() => handleSuggestionClick(sug)}
                        >
                          {/*<span className="material-symbols-outlined suggestion-icon">{sug.icon}</span>*/}
                          <span className="suggestion-text">{sug.value}</span>
                          <span className="suggestion-type">{sug.type === 'skill' ? 'Skill' : 'Job Title'}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="search-input-wrapper">
                  <span className="search-input-icon material-symbols-outlined"> location_on</span>
                  <input
                    type="text"
                    placeholder="City or remote"
                    className="search-input"
                    value={locationQuery}
                    onChange={(e) => setLocationQuery(e.target.value)}
                  />
                </div>

                <button type="submit" className="search-btn">
                  <span className="material-symbols-outlined">search</span>
                  Search Jobs
                </button>
              </form>
              {/* <div className="popular-searches">
                <span>Popular:</span>
                <button onClick={() => navigate('/jobs')} className="tag-btn">Software Engineer</button>
                <button onClick={() => navigate('/jobs')} className="tag-btn">Product Manager</button>
                <button onClick={() => navigate('/jobs')} className="tag-btn">Data Analyst</button>
                <button onClick={() => navigate('/jobs')} className="tag-btn">UI/UX Designer</button>
              </div> */}
            </div>

            {/* <div className="hero-actions">
              <Link to="/register" className="btn btn-primary btn-lg">
                Get Started Free
              </Link>
              <Link to="/jobs" className="btn btn-secondary btn-lg">
                Browse Jobs
              </Link>
            </div> */}
          </div>
        </div>
      </section>

      <section className="features-section">
        <div className="container">
          <h2 className="choose-title">Why Choose HiringOpen?</h2>
          <p className="choose-subtitle">Discover the smarter way to find, connect, and hire the right talent.</p>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon"><span className="material-symbols-outlined">target</span></div>
              <h3>Smart Job Matching</h3>
              <p>AI-powered recommendations based on your skills and preferences</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon"><span className="material-symbols-outlined">business_center</span></div>
              <h3>Top Companies</h3>
              <p>Connect with leading employers across various industries</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon"><span className="material-symbols-outlined">bolt</span></div>
              <h3>Quick Apply</h3>
              <p>Apply to multiple jobs with a <br />single click</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon"><span className="material-symbols-outlined">analytics</span></div>
              <h3>Track Applications</h3>
              <p>Monitor your application status <br />in real-time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      {/* <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{stats.totalJobs}+</div>
              <div className="stat-label">Active Jobs</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{stats.totalCompanies}+</div>
              <div className="stat-label">Top Companies</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{stats.totalCandidates}+</div>
              <div className="stat-label">Job Seekers</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{stats.successfulPlacements}+</div>
              <div className="stat-label">Success Stories</div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Featured Jobs Section */}
      <section className="featured-jobs-section">
        <div className="container">
          <div className="section-header">
            <h2 className="featurd-title">Featured Jobs</h2>
            <p className="featurd-subtitle">Discover hand-picked opportunities from top companies</p>
          </div>

          {loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading jobs...</p>
            </div>
          ) : featuredJobs.length > 0 ? (
            <>
              <div className="jobs-vertical-container">
                {featuredJobs.map((job) => (
                  <div key={job._id} className="job-card-vertical">
                    {job.featured && <span className="badge-featured-vertical">Featured</span>}

                    <div className="job-card-left">
                      <div className="job-header-vertical">
                        <h3 className="job-title-vertical">{job.title}</h3>
                        {
                          job.postedByRole === 'consultant' ? (
                            <p className="job-company-vertical">{job.consultantId?.companyName || job.consultantId?.name}</p>
                          ) : (
                            <p className="job-company-vertical">{job.companyId?.name || 'Company'}</p>
                          )
                        }

                        {
                          job?.showCompanyName && (
                            <span className="company-name-light">
                              For organization: {job.companyId?.name || 'Company'}
                            </span>
                          )
                        }
                      </div>

                      <div className="job-meta-vertical">
                        <span className="meta-item-vertical job-type">
                          <span className="material-symbols-outlined icon">work</span>
                          {job.jobType?.name || 'Full Time'}
                        </span>
                        {
                          job.location?.city?.name && (
                            <span className="meta-item-vertical">
                              <span className="material-symbols-outlined icon">location_on</span>
                              {job.location?.city?.name}
                            </span>
                          )
                        }
                        <span className="meta-item-vertical">
                          <span className="material-symbols-outlined icon">currency_rupee</span>
                          {formatSalary(job)}
                        </span>
                        <span className="meta-item-vertical">
                          <span className="material-symbols-outlined icon">schedule</span>
                          {getTimeAgo(job.createdAt)}
                        </span>
                      </div>

                      {job.skills && job.skills.length > 0 && (
                        <div className="job-skills-vertical">
                          {job.skills.slice(0, 5).map((skill, index) => (
                            <span key={index} className="skill-tag-vertical">
                              {skill.name}
                            </span>
                          ))}
                          {job.skills.length > 5 && (
                            <span className="skill-tag-vertical more-skills">+{job.skills.length - 5}</span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="job-card-right">
                      <Link to={`/jobs/${job._id}`} className="btn-view-job-vertical">
                        View Details
                        <span className="arrow-icon">→</span>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
              <div className="view-more-container">
                <button onClick={() => navigate('/jobs')} className="btn btn-md" style={{border:'1px solid #000', background:'transparent'}}>
                  Explore All Jobs →
                </button>
              </div>
            </>
          ) : (
            <div className="no-jobs-home">
              <div className="no-jobs-content">
                <div className="no-jobs-icon">
                  <span className="material-symbols-outlined">work_outline</span>
                </div>
                <h3 className="no-jobs-title">No Featured Jobs Available</h3>
                <p className="no-jobs-description">
                  We're constantly updating our job listings. Check back soon for exciting opportunities from top companies!
                </p>
                <div className="no-jobs-actions">
                  <Link to="/jobs" className="btn btn-primary btn-lg">
                    <span className="material-symbols-outlined">search</span>
                    Browse All Jobs
                  </Link>
                  <Link to="/companies" className="btn btn-outline btn-lg">
                    <span className="material-symbols-outlined">business</span>
                    Top Companies
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="container flow-bg">
          <h2 className="section-title">How It Works</h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-icon">
                <span className="material-symbols-outlined">person_add</span>
              </div>
              <h3>Create Profile</h3>
              <p>Sign up and build your professional profile with your skills and experience</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-icon">
                <span className="material-symbols-outlined">search</span>
              </div>
              <h3>Search Jobs</h3>
              <p>Browse through thousands of job listings tailored to your preferences</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-icon">
                <span className="material-symbols-outlined">send</span>
              </div>
              <h3>Apply Easily</h3>
              <p>Submit your application with just one click using your profile</p>
            </div>
            <div className="step-card">
              <div className="step-number">4</div>
              <div className="step-icon">
                <span className="material-symbols-outlined">celebration</span>
              </div>
              <h3>Get Hired</h3>
              <p>Connect with employers and land <br />your dream job</p>
            </div>
          </div>
        </div>
      </section>

      {/* Job Categories Section */}
      <section className="categories-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title" style={{textAlign:'center'}}>Browse by Category</h2>
            <p className="section-subtitle" style={{textAlign:'center'}}>
        Explore jobs across different industries and roles
      </p>
          </div>

          <div className="categories-grid">
            {categories.map((category, index) => (
              <div
                className="category-card"
                key={index}
                onClick={() => navigate("/jobs")}
              >
                <h3>{category.title}</h3>

                <p>{category.description}</p>

                <div className="category-bottom">
                  <span className="category-count">
                    {category.count}
                  </span>

                  <div
                    className="category-icon"
                    style={{
                      backgroundColor: category.iconBgColor,
                    }}
                  >
                    <span className="material-symbols-outlined">
                      {category.icon}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted Companies Section */}
      <section className="companies-section">
  <div className="container">
    <h2 className="section-title">Trusted by Leading Companies</h2>
    <p className="section-subtitle">
      Join thousands of companies hiring on HiringOpen
    </p>

    <div className="companies-logos">
      <div className="companies-logos-track">
        {[
          {
            name: "Infosys",
            logo: "/images/infosys.png",
          },
          {
            name: "Wipro",
            logo: "/images/wipro.png",
          },
          {
            name: "HCL",
            logo: "/images/hcl.png",
          },
          {
            name: "TCS",
            logo: "/images/tcs.png",
          },
          {
            name: "JP Morgan",
            logo: "/images/jpmorgan.png",
          },
          {
            name: "Wing Global IT Services Pvt. Ltd.",
            logo: "/images/wing-global.png",
          },
        ].map((company, i) => (
          <div key={i} className="companies-logo-item">
            <img
              src={company.logo}
              alt={company.name}
              className="companies-logo"
            />
          </div>
        ))}

        {/* Duplicate for seamless scrolling */}
        {[
          {
            name: "Infosys",
            logo: "/images/infosys.png",
          },
          {
            name: "Wipro",
            logo: "/images/wipro.png",
          },
          {
            name: "HCL",
            logo: "/images/hcl.png",
          },
          {
            name: "TCS",
            logo: "/images/tcs.png",
          },
          {
            name: "JP Morgan",
            logo: "/images/jpmorgan.png",
          },
          {
            name: "Wing Global IT Services Pvt. Ltd.",
            logo: "/images/wing-global.png",
          },
        ].map((company, i) => (
          <div key={`dup-${i}`} className="companies-logo-item">
            <img
              src={company.logo}
              alt={company.name}
              className="companies-logo"
            />
          </div>
        ))}
      </div>
    </div>
  </div>
</section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <h2 className="section-title">Success Stories</h2>
          <p className="featurd-subtitle">Voices of Growth, Resilience, and Achievement.</p>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"HiringOpen helped me land my dream job at a top tech company. The platform is easy to use and the job recommendations were spot on!"</p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">
                  <span className="material-symbols-outlined">person</span>
                </div>
                <div className="author-info">
                  <h4>Rahul Sharma</h4>
                  <p>Software Engineer</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"As an employer, HiringOpen has been invaluable. We found qualified candidates quickly and the hiring process was seamless."</p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">
                  <span className="material-symbols-outlined">business_center</span>
                </div>
                <div className="author-info">
                  <h4>Priya Patel</h4>
                  <p>HR Manager</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"The best job portal I've used. Got multiple interview calls within a week of creating my profile. Highly recommended!"</p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">
                  <span className="material-symbols-outlined">analytics</span>
                </div>
                <div className="author-info">
                  <h4>Amit Kumar</h4>
                  <p>Data Analyst</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"I found my perfect marketing role through HiringOpen. The platform's filtering options made it easy to find exactly what I was looking for."</p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">
                  <span className="material-symbols-outlined">campaign</span>
                </div>
                <div className="author-info">
                  <h4>Neha Gupta</h4>
                  <p>Marketing Specialist</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"The quality of candidates on HiringOpen is exceptional. We've hired three amazing team members through this platform."</p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">
                  <span className="material-symbols-outlined">groups</span>
                </div>
                <div className="author-info">
                  <h4>Vikram Singh</h4>
                  <p>Tech Lead</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"From application to offer in just two weeks! HiringOpen made my job search journey smooth and successful."</p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">
                  <span className="material-symbols-outlined">rocket_launch</span>
                </div>
                <div className="author-info">
                  <h4>Kavya Reddy</h4>
                  <p>Product Designer</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {/* <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-vertical-container">
            <div className="faq-card-vertical">
              <div className="faq-icon">❓</div>
              <div className="faq-content">
                <h3 className="faq-question">How do I create an account?</h3>
                <p className="faq-answer">Click on "Get Started" button, fill in your details, and verify your email. It's completely free!</p>
              </div>
            </div>
            <div className="faq-card-vertical">
              <div className="faq-icon">💰</div>
              <div className="faq-content">
                <h3 className="faq-question">Is HiringOpen free to use?</h3>
                <p className="faq-answer">Yes! Job seekers can create profiles, search jobs, and apply for free. We also offer premium features for enhanced visibility.</p>
              </div>
            </div>
            <div className="faq-card-vertical">
              <div className="faq-icon">📝</div>
              <div className="faq-content">
                <h3 className="faq-question">How do I apply for jobs?</h3>
                <p className="faq-answer">Browse jobs, click on any listing, and hit the "Apply" button. Your profile information will be sent to the employer.</p>
              </div>
            </div>
            <div className="faq-card-vertical">
              <div className="faq-icon">💼</div>
              <div className="faq-content">
                <h3 className="faq-question">Can employers contact me directly?</h3>
                <p className="faq-answer">Yes! With a complete profile, employers can reach out to you directly with relevant opportunities.</p>
              </div>
            </div>
            <div className="faq-card-vertical">
              <div className="faq-icon">🔍</div>
              <div className="faq-content">
                <h3 className="faq-question">How do I search for jobs?</h3>
                <p className="faq-answer">Use our advanced search filters to find jobs by location, type, salary range, and skills that match your profile.</p>
              </div>
            </div>
            <div className="faq-card-vertical">
              <div className="faq-icon">⏰</div>
              <div className="faq-content">
                <h3 className="faq-question">How long does it take to get hired?</h3>
                <p className="faq-answer">The hiring timeline varies by company, but most candidates hear back within 1-2 weeks after applying.</p>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Newsletter Section */}
     {/*  <section className="newsletter-section">
        <div className="container">
          <div className="newsletter-content">
            <div className="newsletter-text">
              <h2>Stay Updated</h2>
              <p>Subscribe to our newsletter and get the latest job openings delivered to your inbox</p>
            </div>
            <form className="newsletter-form" onSubmit={handleNewsletterSubscribe}>
              <input
                type="email"
                placeholder="Enter your email address"
                className="newsletter-input"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                disabled={subscribing}
              />
              <button
                type="submit"
                className="newsletter-btn"
                disabled={subscribing}
              >
                {subscribing ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      {/* <section className="cta-section">
        <div className="container">
          <h2>Ready to Take the Next Step?</h2>
          <p>Join thousands of job seekers and employers on HiringOpen</p>
          <div className="cta-buttons">
            <Link to="/register" className="btn btn-primary btn-lg">
              Create Free Account
            </Link>
            <Link to="/jobs" className="btn btn-outline-white btn-lg">
              Explore Jobs
            </Link>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default Home;
